/**
 * Runtime configuration. The deploy workflow writes /config.json from the Terraform `web_config`
 * output, so one build works for any environment. For local development copy
 * public/config.example.json to public/config.json, or run
 *   terraform -chdir=infra/live output -raw web_config > apps/web/public/config.json
 */
export interface AppConfig {
  region: string
  apiUrl: string
  aiUrl: string
  dataBucketHost: string
}

const REQUIRED_KEYS: (keyof AppConfig)[] = ['region', 'apiUrl', 'aiUrl', 'dataBucketHost']

export function parseConfig(value: unknown): AppConfig {
  if (typeof value !== 'object' || value === null) {
    throw new Error('config.json must contain a JSON object')
  }
  const record = value as Record<string, unknown>
  const missing = REQUIRED_KEYS.filter((key) => typeof record[key] !== 'string' || record[key] === '')
  if (missing.length > 0) {
    throw new Error(`config.json is missing: ${missing.join(', ')}`)
  }
  const strip = (url: string) => url.replace(/\/+$/, '')
  return {
    region: record.region as string,
    apiUrl: strip(record.apiUrl as string),
    aiUrl: strip(record.aiUrl as string),
    dataBucketHost: record.dataBucketHost as string,
  }
}

export async function loadConfig(fetchImpl: typeof fetch = fetch): Promise<AppConfig> {
  const response = await fetchImpl('/config.json', { cache: 'no-store' })
  if (!response.ok) {
    throw new Error(`could not load /config.json (HTTP ${response.status})`)
  }
  return parseConfig(await response.json())
}
